import PropTypes from 'prop-types';
import { createContext } from 'react';
import { deepMap } from 'nanostores';

const initStore = (allStores, id, initialState = {}) => {
  const storeExists = allStores.has(id);

  const store = storeExists ? allStores.get(id).store : deepMap({});

  if (!storeExists) {
    store.set(initialState);
    allStores.set(id, { store });
    return store;
  }

  // Handle case where state is already in store to stop react/nanostores react hook from re-rendering components
  const currentState = store.get();
  const stateIsAlreadyInStore = Object.keys(initialState).every(
    (key) => currentState[key] !== undefined,
  );

  if (!stateIsAlreadyInStore) {
    store.set({ ...store.get(), ...initialState });
  }

  return store;
};

// Client vanillaJs support
const clientStoreCollections = new Map();

export const getStoreClient = (id) => {
  const storeCollection = clientStoreCollections.get(id);

  if (!storeCollection) {
    return null;
  }

  const { store } = storeCollection;

  const getValue = (key) => {
    const value = store.get()[key];
    return value;
  };

  const setValue = (key, valueOrCallback) => {
    let value = valueOrCallback;
    if (typeof valueOrCallback === 'function') {
      const prev = getValue(key);
      value = valueOrCallback(prev);
    }
    store.setKey(key, value);
  };

  const storeApiEntries = Object.keys(store.get()).map((key) => {
    return [
      key,
      {
        getValue: () => getValue(key),
        setValue: (callbackOrValue) => setValue(key, callbackOrValue),
      },
    ];
  });

  return {
    store: {
      ...store,
      // Internal API for entries in store so we can use prev callback when setting values
      internalApi: Object.fromEntries(storeApiEntries),
    },
  };
};

// React Client & Server support
export const StoreContext = createContext(null);
export const StoreProvider = ({ isServer, children }) => {
  const storeCollections = isServer ? new Map() : clientStoreCollections;

  // Store collections and these methods never change
  // No need for memo
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    initStore: (...args) => initStore(storeCollections, ...args),
  };

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
};

StoreProvider.propTypes = {
  isServer: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};